import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  CircleDollarSignIcon,
  ReceiptIcon,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useFetchEquipmentTypes } from 'hooks/useFetchEquipmentTypes';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { createLocation } from 'lib/api/createLocation';
import { getCargoValueDefault } from 'lib/api/getCargoValueDefault';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { fetchCustomerByExternalTMSID } from 'lib/api/searchCustomers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { ExternalLinks } from 'types/ExternalLinks';
import {
  AdditionalReference,
  Commodity,
  CostLineItem,
  Load,
  LoadStopTypes,
  NormalizedLoad,
  Note,
  TMSCustomer,
  TMSEquipmentType,
  TMSLocation,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  createNewCommodity,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { normalizeExternalTMSID } from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';
import { parseToUtcWallClockDate } from 'utils/parseToUtcWallClockDate';

import DuplicateLoadWarningDialog from '../DuplicateLoadWarningDialog';
import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../utils/stopValidation';
import { BillToSectionForm } from './BillTo';
import { CustomerSectionForm } from './Customer';
import { RatesForm } from './Rates';
import { SpecificationsForm } from './Specifications';
import { StopEntryMode, StopForm, hasValidAISuggestedLocation } from './Stop';
import { RevenovaTenant } from './constants';
import {
  getModeOptions,
  getTenantFromString,
  mapApiModeToFormModeValue,
  normalizeServiceType,
} from './helpers';
import {
  isBlankRevenovaCommodity,
  normalizeCommoditiesForRevenovaUI,
  normalizeCommodityForRevenovaAPI,
} from './normalizeCommodities';
import {
  cleanStopDataForQuickEntry,
  mergeOrReplaceLocationInList,
} from './stopLocationHelpers';
import {
  deriveModeFromTransportType,
  isEquipmentRefrigerated,
  resolveAppointmentSuggestion,
} from './utils';

enum AvailableTabs {
  customer = 'customer',
  specifications = 'specifications',
  rates = 'rates',
  pickup = 'pickup',
  consignee = 'consignee',
  billTo = 'billTo',
}

const initLoadBuildingForm = (tmsName: string): NormalizedLoad =>
  normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'TL', // Required as field is read-only
    specifications: {
      ...createInitSpecs(),
      transportType: 'Van',
      serviceType: 'Spot',
    },
    rateData: {
      ...createInitRateData(),
      customerLineHaulCharge: {
        val: 0,
        unit: Unit.USD,
      },
      fscFlatRate: 0,
      customerTotalCharge: {
        val: 0,
        unit: Unit.USD,
      },
    },
  });

/**
 * Checks if an error message is related to customer quote account status.
 * This handles expected failures when customer quotes cannot be created due to account restrictions.
 */
function isCustomerQuoteAccountStatusError(message: Undef<string>): boolean {
  if (!message) {
    return false;
  }

  return message.toLowerCase().includes('failed to create customer quote');
}

/**
 * Gets the display message for customer quote account status errors.
 */
function getCustomerQuoteAccountStatusMessage(loadId: Maybe<string>): {
  title: string;
  description: string;
} {
  const loadIdText = loadId ? `Load ${loadId}` : 'Load';
  return {
    title: `${loadIdText} was created successfully`,
    description:
      'A customer quote could not be created because the customer account is not currently in a quotable state in Revenova.\n\n' +
      'This can occur when an account is on credit hold, not fully approved, or requires review based on internal account rules.\n\n' +
      'Please review the customer account or follow your normal escalation process if a quote is required.',
  };
}

/**
 * Formats toast title and description for display, handling customer quote account status errors specially.
 * Returns formatted title and description (as ReactNode) for use in toast notifications.
 */
function formatToastMessage(
  message: Undef<string>,
  loadId: Maybe<string>
): {
  title: string;
  description: React.ReactNode;
} {
  const isCustomerQuoteError = isCustomerQuoteAccountStatusError(message);
  const customerQuoteMessage = isCustomerQuoteError
    ? getCustomerQuoteAccountStatusMessage(loadId)
    : null;

  const title = customerQuoteMessage
    ? customerQuoteMessage.title
    : message || '';

  let description: React.ReactNode;
  if (customerQuoteMessage) {
    description = (
      <span className='whitespace-pre-line'>
        {customerQuoteMessage.description}
      </span>
    );
  } else if (loadId) {
    description = `Load ID: ${loadId}`;
  } else {
    description = undefined;
  }

  return { title, description };
}

function toastVariantFromErrorLevel(
  errorLevel: Undef<string>
): 'warning' | 'destructive' | 'success' {
  if (errorLevel === 'warning') {
    return 'warning';
  }
  if (errorLevel === 'error') {
    return 'destructive';
  }
  return 'success';
}

function submitButtonLabel(
  isLoading: boolean,
  isCreateMode: boolean
): React.ReactNode {
  if (isLoading) {
    return <ButtonLoader />;
  }
  if (isCreateMode) {
    return ButtonText.BuildLoad;
  }
  return ButtonText.UpdateTMS;
}

/**
 * When the AI detects multiple reference numbers (e.g. "1 of 3", "2 of 3", "3 of 3"),
 * returns a comma-separated string of all unique ref numbers for auto-applying to BOL, PO,
 * stop refs, and line item ref fields. Returns undefined when there are fewer than 2 ref numbers.
 */
function getAutoAppliedRefNumbersString(
  suggestion: SuggestedLoad
): Undef<string> {
  const fromCust =
    normalizeToStringArray(suggestion.customer?.refNumberCandidates) ?? [];
  const fromPickup =
    normalizeToStringArray(suggestion.pickup?.refNumberCandidates) ?? [];
  const fromConsignee =
    normalizeToStringArray(suggestion.consignee?.refNumberCandidates) ?? [];
  const fromAdditional = (
    normalizeToArray(suggestion.additionalReferences) ?? []
  )
    .map((r) => r?.number)
    .filter(Boolean) as string[];
  // Split comma-separated ref fields (e.g. poNums "PO123, PO456") into individual refs so they are included
  const fromRefFields = [
    suggestion.customer?.refNumber,
    suggestion.pickup?.refNumber,
    suggestion.consignee?.refNumber,
    suggestion.poNums,
  ]
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .flatMap((s) =>
      s
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
    );

  const merged = [
    ...new Set(
      [
        ...fromCust,
        ...fromPickup,
        ...fromConsignee,
        ...fromAdditional,
        ...fromRefFields,
      ]
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    ),
  ];

  if (merged.length < 2) return undefined;
  return merged.join(', ');
}

function shouldDefaultStopToLocationEntry(
  stop: Undef<{
    externalTMSID?: string;
    name?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }>
): boolean {
  if (!stop) {
    return false;
  }
  if (stop.externalTMSID) {
    return true;
  }
  return !!(
    stop.name &&
    stop.addressLine1 &&
    stop.city &&
    stop.state &&
    stop.zipCode
  );
}

function applyRevenovaCargoInsuranceValue(
  normalizedLoad: NormalizedLoad,
  sourceLoad: Maybe<Load>
): NormalizedLoad {
  const cargoValue = (sourceLoad as Maybe<Load & { cargoValue?: number }>)
    ?.cargoValue;
  const resolvedCargoInsuranceUSD =
    normalizedLoad.declaredValueUSD ?? cargoValue;

  return {
    ...normalizedLoad,
    declaredValueUSD: resolvedCargoInsuranceUSD ?? undefined,
  };
}

interface UnifiedRevenovaLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onInvalid?: SubmitErrorHandler<NormalizedLoad>;
  externalLinks?: ExternalLinks;
}

export default function UnifiedRevenovaLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onInvalid,
  externalLinks: _externalLinks,
}: UnifiedRevenovaLoadFormProps) {
  const { serviceID, tmsIntegrations } = useServiceFeatures();
  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const { tenant, tmsID } = useTMSContext();
  const tmsName = TMS.Revenova;
  const { equipmentTypes } = useFetchEquipmentTypes(tmsID);
  const [isLoading, setIsLoading] = useState(false);
  // billTo is edit-only; omit so Expand/Collapse isn't stuck on a ghost tab.
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.specifications,
      AvailableTabs.rates,
      AvailableTabs.pickup,
      AvailableTabs.consignee,
    ];
    if (!isCreateMode) {
      tabs.push(AvailableTabs.billTo);
    }
    return tabs;
  }, [isCreateMode]);
  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [fetchedCustomers, setFetchedCustomers] =
    useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);
  const [builtLoadId, setBuiltLoadId] = useState<Maybe<string>>(null);
  const [showDuplicateWarningDialog, setShowDuplicateWarningDialog] =
    useState(false);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<Maybe<NormalizedLoad>>(null);

  // Track entry mode for pickup and consignee to clean data appropriately on submit
  const [pickupEntryMode, setPickupEntryMode] = useState<StopEntryMode>(
    StopEntryMode.Location
  );
  const [consigneeEntryMode, setConsigneeEntryMode] = useState<StopEntryMode>(
    StopEntryMode.Location
  );

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const originalLoadBuildingSuggestion = useMemo((): Undef<NormalizedLoad> => {
    if (
      !isCreateMode ||
      !clickedSuggestion ||
      clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
    ) {
      return undefined;
    }
    return clickedSuggestion.suggested as NormalizedLoad;
  }, [isCreateMode, clickedSuggestion]);

  // Get mode options based on tenant
  const modeOptions = useMemo(() => {
    return getModeOptions(tenant);
  }, [tenant]);

  const isCoolerTenant = tenant
    ? getTenantFromString(tenant) === RevenovaTenant.Cooler
    : false;

  const fetchCustomers = useCallback(async () => {
    setIsLoadingCustomers(true);
    const res = await getCustomers(tmsIntegrations[0]?.id);
    if (res.isOk()) {
      setFetchedCustomers((prev) => {
        let newList = res.value.customerList ?? [];
        if (prev) {
          prev.forEach((p) => {
            newList = injectSelectedObject(p, newList);
          });
        }
        return newList;
      });
    } else {
      toast({
        description: res.error.message || 'Error while fetching customer list.',
        variant: 'destructive',
      });
    }
    setIsLoadingCustomers(false);
  }, [tmsIntegrations, toast]);

  const fetchLocations = useCallback(async () => {
    setIsLoadingLocations(true);
    const res = await getLocations(tmsIntegrations[0]?.id);
    if (res.isOk()) {
      setLocations(res.value.locationList);
    } else {
      toast({
        description: res.error.message || 'Error while fetching location list.',
        variant: 'destructive',
      });
    }
    setIsLoadingLocations(false);
  }, [tmsIntegrations, toast]);

  useEffect(() => {
    void fetchCustomers();
    void fetchLocations();
  }, [fetchCustomers, fetchLocations]);

  useEffect(() => {
    // Only auto-navigate to suggestions in create mode
    if (isCreateMode && !clickedSuggestion) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
  }, [isCreateMode, clickedSuggestion, goToSuggestion]);

  useEffect(() => {
    // Update suggestion clicked state (only for create mode)
    if (isCreateMode) {
      if (
        clickedSuggestion &&
        clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        setIsLoadBuildingSuggestionClicked(true);
        setShowAIHints(true);
      } else {
        setIsLoadBuildingSuggestionClicked(false);
        setShowAIHints(false);
      }
    }
  }, [isCreateMode, clickedSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
    setBuiltLoadId(null);
  }, [clickedSuggestion?.id]);

  useEffect(() => {
    // Handle form state restoration from storage (only for create mode)
    if (isCreateMode) {
      const savedState = FormStorageService.getFormState<
        NormalizedLoad,
        NormalizedLoad
      >(`${tmsName}_${threadId}`);
      if (
        savedState &&
        savedState.threadID === threadId &&
        clickedSuggestion == null
      ) {
        setShowAIHints(true);
        goToSuggestion({
          suggestionID: savedState.clickedSuggestion?.id,
        });
      }
    }
  }, [isCreateMode, threadId, clickedSuggestion, goToSuggestion, tmsName]);

  // Key defaults off suggestion id (create) / thread so same-id refreshes do not
  // rebuild defaults and wipe in-progress edits via the reset effect below.
  const defaultsSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;
  const defaultsThreadId = isCreateMode ? threadId : null;
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    let baseLoad: NormalizedLoad;

    // Prefer create-load API response over suggestion defaults. Otherwise the suggestion branch
    // can win while isCreateMode && clickedSuggestion are still true and drop user-edited fields
    // that only exist on the saved load (e.g. mustDeliver set in the form but absent/invalid on AI).
    // Key off defaultsSuggestionId / defaultsThreadId (not the suggestion object) so same-id
    // refreshes do not rebuild defaults and wipe edits.
    const clickedSuggestion =
      defaultsSuggestionId != null ? clickedSuggestionRef.current : null;
    void defaultsThreadId;

    if (builtLoad) {
      baseLoad = applyRevenovaCargoInsuranceValue(
        normalizeLoad(tmsName, builtLoad),
        builtLoad
      );

      if (baseLoad.commodities && Array.isArray(baseLoad.commodities)) {
        baseLoad.commodities = normalizeCommoditiesForRevenovaUI(
          baseLoad.commodities,
          baseLoad
        );
      }

      if (baseLoad.rateData) {
        const lineHaulVal =
          baseLoad.rateData.customerLineHaulCharge?.val != null
            ? baseLoad.rateData.customerLineHaulCharge.val
            : 0;
        const fscVal =
          baseLoad.rateData.fscFlatRate != null
            ? baseLoad.rateData.fscFlatRate
            : 0;
        const computedTotal =
          (Number(lineHaulVal) || 0) + (Number(fscVal) || 0);

        baseLoad.rateData = {
          ...baseLoad.rateData,
          fscFlatRate: Number(baseLoad.rateData.fscFlatRate) || 0,
          customerLineHaulCharge: baseLoad.rateData.customerLineHaulCharge
            ? {
                ...baseLoad.rateData.customerLineHaulCharge,
                val: Number(baseLoad.rateData.customerLineHaulCharge.val) || 0,
              }
            : {
                val: 0,
                unit: Unit.USD,
              },
          customerTotalCharge: baseLoad.rateData.customerTotalCharge
            ? {
                ...baseLoad.rateData.customerTotalCharge,
                val: computedTotal,
                unit: baseLoad.rateData.customerTotalCharge.unit ?? Unit.USD,
              }
            : {
                val: computedTotal,
                unit: Unit.USD,
              },
        };
      }
    } else if (
      isCreateMode &&
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      const castedClickedSuggestion =
        clickedSuggestion.suggested as SuggestedLoad;
      const transportType =
        castedClickedSuggestion.specifications?.transportType ?? '';

      // Derive mode from transport type (defaults to TL if no specific mapping)
      const derivedMode = deriveModeFromTransportType(transportType, tenant);

      const customerLineItemsArray: CostLineItem[] =
        normalizeToArray(castedClickedSuggestion.rateData?.customerLineItems) ||
        [];
      const carrierLineItemsArray: CostLineItem[] =
        normalizeToArray(castedClickedSuggestion.rateData?.carrierLineItems) ||
        [];

      const commoditiesArray: Commodity[] =
        normalizeToArray(castedClickedSuggestion.commodities) || [];

      if (
        commoditiesArray.length === 0 &&
        castedClickedSuggestion.specifications?.commodities
      ) {
        commoditiesArray.push({
          ...createNewCommodity(),
          description: castedClickedSuggestion.specifications.commodities,
        });
      }

      const additionalReferencesArray: AdditionalReference[] =
        normalizeToArray(castedClickedSuggestion.additionalReferences) || [];
      const notesArray: Note[] =
        normalizeToArray(castedClickedSuggestion.notes) || [];

      // When AI detects multiple reference numbers (e.g. "1 of 3"), auto-apply all to BOL, PO, stops, and line items (Cooler tenant only)
      const autoAppliedRefNumbers = isCoolerTenant
        ? getAutoAppliedRefNumbersString(castedClickedSuggestion)
        : undefined;

      const suggestedFields = {
        ...castedClickedSuggestion,
        mode: derivedMode,
        commodities: commoditiesArray,
        additionalReferences: additionalReferencesArray,
        notes: notesArray,
        // Preserve refNumberCandidates fields
        customer: {
          ...castedClickedSuggestion.customer,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.customer?.refNumberCandidates
          ),
          ...(autoAppliedRefNumbers && { refNumber: autoAppliedRefNumbers }),
        },
        pickup: {
          ...castedClickedSuggestion.pickup,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.pickup?.refNumberCandidates
          ),
          ...(autoAppliedRefNumbers && { refNumber: autoAppliedRefNumbers }),
          readyTime:
            parseToUtcWallClockDate(
              castedClickedSuggestion.pickup?.readyTime
            ) ?? undefined,
          ...resolveAppointmentSuggestion({
            tenant,
            ...castedClickedSuggestion.pickup,
            apptStartTime: castedClickedSuggestion.pickup?.apptStartTime,
            apptEndTime: castedClickedSuggestion.pickup?.apptEndTime,
            fallbackTime: castedClickedSuggestion.pickup?.readyTime,
          }),
        },
        consignee: {
          ...castedClickedSuggestion.consignee,
          refNumberCandidates: normalizeToStringArray(
            castedClickedSuggestion.consignee?.refNumberCandidates
          ),
          ...(autoAppliedRefNumbers && { refNumber: autoAppliedRefNumbers }),
          mustDeliver:
            parseToUtcWallClockDate(
              castedClickedSuggestion.consignee?.mustDeliver
            ) ?? undefined,
          ...resolveAppointmentSuggestion({
            tenant,
            ...castedClickedSuggestion.consignee,
            apptStartTime: castedClickedSuggestion.consignee?.apptStartTime,
            apptEndTime: castedClickedSuggestion.consignee?.apptEndTime,
            fallbackTime: castedClickedSuggestion.consignee?.mustDeliver,
          }),
        },
        specifications: {
          ...createInitSpecs(),
          ...castedClickedSuggestion.specifications,
          serviceType: normalizeServiceType(
            castedClickedSuggestion.specifications?.serviceType
          ),
          // Title-case the transport type for display (LLM may return "REEFER" in all caps).
          // No equipment in the email stays empty so the rep picks one.
          transportType: suggestedTransportTypeOrEmpty(
            castedClickedSuggestion.specifications
          )
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          // Round up pallets to nearest integer (LLM may return decimals)
          totalOutPalletCount: castedClickedSuggestion.specifications
            ?.totalOutPalletCount
            ? Math.ceil(
                castedClickedSuggestion.specifications.totalOutPalletCount
              )
            : undefined,
        },
        rateData: {
          ...createInitRateData(),
          ...castedClickedSuggestion.rateData,
          customerTotalCharge: {
            ...castedClickedSuggestion.rateData.customerTotalCharge,
            unit: Unit.USD,
          },
          customerLineItems: customerLineItemsArray,
          carrierLineItems: carrierLineItemsArray,
        },
      };
      setShowAIHints(true);

      baseLoad = {
        ...normalizeLoad(tmsName, createInitLoad()),
        ...suggestedFields,
        mode: derivedMode,
        ...(autoAppliedRefNumbers && { poNums: autoAppliedRefNumbers }),
        rateData: {
          ...suggestedFields.rateData,
          customerTotalCharge: {
            ...suggestedFields.rateData.customerTotalCharge,
            unit: Unit.USD,
          },
        },
      } as NormalizedLoad;
      // Ensure charge fields default to 0 if null/undefined for consistency with create mode
      // Compute total (line haul + fuel) so initial population shows correct Total Amount
      if (baseLoad.rateData) {
        const lineHaulVal =
          baseLoad.rateData.customerLineHaulCharge?.val != null
            ? baseLoad.rateData.customerLineHaulCharge.val
            : 0;
        const fscVal =
          baseLoad.rateData.fscFlatRate != null
            ? baseLoad.rateData.fscFlatRate
            : 0;
        const computedTotal =
          (Number(lineHaulVal) || 0) + (Number(fscVal) || 0);
        baseLoad.rateData = {
          ...baseLoad.rateData,
          fscFlatRate:
            baseLoad.rateData.fscFlatRate === null ||
            baseLoad.rateData.fscFlatRate === undefined
              ? 0
              : Number(baseLoad.rateData.fscFlatRate) || 0,
          customerLineHaulCharge: baseLoad.rateData.customerLineHaulCharge
            ? {
                ...baseLoad.rateData.customerLineHaulCharge,
                val:
                  baseLoad.rateData.customerLineHaulCharge.val === null ||
                  baseLoad.rateData.customerLineHaulCharge.val === undefined
                    ? 0
                    : Number(baseLoad.rateData.customerLineHaulCharge.val) || 0,
              }
            : {
                val: 0,
                unit: Unit.USD,
              },
          customerTotalCharge: baseLoad.rateData.customerTotalCharge
            ? {
                ...baseLoad.rateData.customerTotalCharge,
                val: computedTotal,
                unit: baseLoad.rateData.customerTotalCharge.unit ?? Unit.USD,
              }
            : {
                val: computedTotal,
                unit: Unit.USD,
              },
        };
      }
    } else if (!isCreateMode && load) {
      // Handle edit mode with existing load data
      baseLoad = applyRevenovaCargoInsuranceValue(
        normalizeLoad(tmsName, load),
        load
      );

      // Normalize empty strings in commodities unit fields to undefined
      // This is critical: Select components cannot handle empty string values
      // Also map weight units from API response
      if (baseLoad.commodities && Array.isArray(baseLoad.commodities)) {
        baseLoad.commodities = normalizeCommoditiesForRevenovaUI(
          baseLoad.commodities,
          baseLoad
        );
      }

      // Ensure charge fields default to 0 if null/undefined for consistency with create mode
      // Create a copy of rateData to avoid mutating the original object (edit mode: preserve existing total from load)
      if (baseLoad.rateData) {
        baseLoad.rateData = {
          ...baseLoad.rateData,
          fscFlatRate:
            baseLoad.rateData.fscFlatRate === null ||
            baseLoad.rateData.fscFlatRate === undefined
              ? 0
              : baseLoad.rateData.fscFlatRate,
          customerLineHaulCharge: baseLoad.rateData.customerLineHaulCharge
            ? {
                ...baseLoad.rateData.customerLineHaulCharge,
                val:
                  baseLoad.rateData.customerLineHaulCharge.val === null ||
                  baseLoad.rateData.customerLineHaulCharge.val === undefined
                    ? 0
                    : baseLoad.rateData.customerLineHaulCharge.val,
              }
            : {
                val: 0,
                unit: Unit.USD,
              },
          customerTotalCharge: baseLoad.rateData.customerTotalCharge
            ? {
                ...baseLoad.rateData.customerTotalCharge,
                val:
                  baseLoad.rateData.customerTotalCharge.val === null ||
                  baseLoad.rateData.customerTotalCharge.val === undefined
                    ? 0
                    : baseLoad.rateData.customerTotalCharge.val,
              }
            : {
                val: 0,
                unit: Unit.USD,
              },
        };
      }
    } else {
      baseLoad = initLoadBuildingForm(tmsName);
    }

    const modeForForm = mapApiModeToFormModeValue(baseLoad.mode, tenant);
    if (modeForForm !== undefined) {
      baseLoad = { ...baseLoad, mode: modeForForm };
    }

    return baseLoad;
  }, [
    builtLoad,
    isCreateMode,
    load,
    tmsName,
    tenant,
    isCoolerTenant,
    defaultsSuggestionId,
    defaultsThreadId,
  ]);

  // Compute customers list that includes customer from load if it exists
  // This ensures the customer is always in the list when the form renders
  const customers = useMemo(() => {
    if (!memoizedDefaultValues?.customer?.externalTMSID) {
      return fetchedCustomers;
    }
    return injectSelectedObject(
      memoizedDefaultValues.customer as TMSCustomer,
      fetchedCustomers ?? []
    );
  }, [fetchedCustomers, memoizedDefaultValues?.customer]);

  const setCustomers = setFetchedCustomers;

  // Inject pickup and consignee locations when suggestion/load data exists but the
  // hydrated TMS list is missing or sparse (common on mount and after refresh).
  useEffect(() => {
    if (isLoadingLocations) {
      return;
    }

    const stopsToInject: TMSLocation[] = [];
    for (const stop of [
      memoizedDefaultValues.pickup,
      memoizedDefaultValues.consignee,
    ]) {
      if (!stop?.name?.trim()) {
        continue;
      }
      if (
        normalizeExternalTMSID(stop.externalTMSID) ||
        stop.addressLine1?.trim()
      ) {
        stopsToInject.push(stop as TMSLocation);
      }
    }

    if (stopsToInject.length === 0) {
      return;
    }

    setLocations((prevLocations) => {
      let updatedLocations = prevLocations ?? [];
      stopsToInject.forEach((loc) => {
        updatedLocations = mergeOrReplaceLocationInList(updatedLocations, loc);
      });
      return updatedLocations;
    });
  }, [
    isLoadingLocations,
    memoizedDefaultValues?.pickup,
    memoizedDefaultValues?.pickup?.externalTMSID,
    memoizedDefaultValues?.pickup?.name,
    memoizedDefaultValues?.pickup?.addressLine1,
    memoizedDefaultValues?.pickup?.city,
    memoizedDefaultValues?.pickup?.state,
    memoizedDefaultValues?.pickup?.zipCode,
    memoizedDefaultValues?.consignee,
    memoizedDefaultValues?.consignee?.externalTMSID,
    memoizedDefaultValues?.consignee?.name,
    memoizedDefaultValues?.consignee?.addressLine1,
    memoizedDefaultValues?.consignee?.city,
    memoizedDefaultValues?.consignee?.state,
    memoizedDefaultValues?.consignee?.zipCode,
  ]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });
  const customer = useWatch({
    control: formMethods.control,
    name: 'customer',
  });
  const customerName = customer?.name;
  const prevCustomerIdRef = useRef<Maybe<string>>(null);

  useEffect(() => {
    // Auto-default cargo insurance only for create flow.
    // In update/view flows, always preserve load GET API value (or empty value) as-is.
    if (!isCreateMode) return;

    const currentCustomerId = customer?.externalTMSID ?? null;
    if (!currentCustomerId || !customerName) return;

    if (currentCustomerId === prevCustomerIdRef.current) return;
    prevCustomerIdRef.current = currentCustomerId;

    // The backend owns the per-tenant default (null: the tenant has none). On failure, leave
    // the field as is; create applies the same default when the load has no declared value.
    getCargoValueDefault(tmsID, customerName).then((result) => {
      if (result.isErr() || result.value === null) return;
      // Ignore a response for a customer the rep has since changed.
      if (prevCustomerIdRef.current !== currentCustomerId) return;

      formMethods.setValue('declaredValueUSD', result.value, {
        shouldDirty: true,
      });
    });
  }, [isCreateMode, customer?.externalTMSID, customerName, formMethods, tmsID]);

  function handleClearForm() {
    setShowAIHints(false);
    setBuiltLoad(null);
    setBuiltLoadId(null);
    setPendingSubmissionData(null);
    setShowDuplicateWarningDialog(false);
    FormStorageService.clearFormState(`${tmsName}_${threadId}`);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));
    formMethods.reset(initLoadBuildingForm(tmsName), {
      keepDefaultValues: false,
      keepDirty: false,
      keepDirtyValues: false,
      keepTouched: false,
    });
    prevCustomerIdRef.current = null;
  }

  // Persist form state on every change (only for create mode)
  useEffect(() => {
    if (isCreateMode) {
      const subscription = formMethods.watch((value) => {
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          values: value,
          clickedSuggestion: isLoadBuildingSuggestionClicked
            ? clickedSuggestion
            : null,
          dirtyFields: formMethods.formState.dirtyFields,
        });
      });
      return () => subscription.unsubscribe();
    }
    return undefined;
  }, [
    isCreateMode,
    formMethods,
    threadId,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    tmsName,
  ]);

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  // Reset form when default values change
  useEffect(() => {
    if (!memoizedDefaultValues) {
      return;
    }
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });

    return scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    formMethods,
  ]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  // Resolve full customer by externalTMSID when load/suggestion/builtLoad only provides the ID.
  // Injects resolved customer into fetchedCustomers so dropdown and read-only fields populate.
  // Read customers via ref so list updates do not cancel an in-flight resolve (deps omit the
  // array); lastResolvedCustomerRef still dedupes; setFetchedCustomers uses a functional updater.
  const lastResolvedCustomerRef = useRef<{
    tmsID: number;
    externalTMSID: string;
  } | null>(null);
  const fetchedCustomersRef = useRef(fetchedCustomers);
  fetchedCustomersRef.current = fetchedCustomers;
  useEffect(() => {
    const tmsID = tmsIntegrations?.[0]?.id;
    const externalId = memoizedDefaultValues?.customer?.externalTMSID?.trim();
    if (!tmsID || !externalId) {
      return;
    }
    const customerIdStr = String(externalId);
    const customersList = fetchedCustomersRef.current ?? [];
    const existingCustomer = customersList.find(
      (c) =>
        c.externalTMSID != null &&
        String(c.externalTMSID).trim() === customerIdStr
    );
    const isDisplayComplete = (c: TMSCustomer) =>
      c?.name != null && String(c.name).trim() !== '';

    if (existingCustomer && isDisplayComplete(existingCustomer)) {
      return;
    }

    if (
      lastResolvedCustomerRef.current?.tmsID === tmsID &&
      lastResolvedCustomerRef.current?.externalTMSID === customerIdStr
    ) {
      return;
    }

    let runCancelled = false;
    let runFinished = false;
    lastResolvedCustomerRef.current = { tmsID, externalTMSID: customerIdStr };

    fetchCustomerByExternalTMSID(tmsID, externalId).then((res) => {
      runFinished = true;
      if (runCancelled) return;

      if (res.isErr() || !res.value?.externalTMSID) {
        if (
          lastResolvedCustomerRef.current?.tmsID === tmsID &&
          lastResolvedCustomerRef.current?.externalTMSID === customerIdStr
        ) {
          lastResolvedCustomerRef.current = null;
        }
        return;
      }

      const customer = res.value;
      setFetchedCustomers((prev) => injectSelectedObject(customer, prev ?? []));
      Object.entries(customer).forEach(([key, value]) => {
        formMethods.setValue(
          `customer.${key}` as FieldPath<NormalizedLoad>,
          value,
          { shouldDirty: false }
        );
      });
    });

    return () => {
      runCancelled = true;
      if (
        !runFinished &&
        lastResolvedCustomerRef.current?.tmsID === tmsID &&
        lastResolvedCustomerRef.current?.externalTMSID === customerIdStr
      ) {
        lastResolvedCustomerRef.current = null;
      }
    };
  }, [
    tmsIntegrations,
    memoizedDefaultValues?.customer?.externalTMSID,
    formMethods,
  ]);

  useEffect(() => {
    setPickupEntryMode(
      shouldDefaultStopToLocationEntry({
        externalTMSID: memoizedDefaultValues?.pickup?.externalTMSID,
        name: memoizedDefaultValues?.pickup?.name,
        addressLine1: memoizedDefaultValues?.pickup?.addressLine1,
        city: memoizedDefaultValues?.pickup?.city,
        state: memoizedDefaultValues?.pickup?.state,
        zipCode: memoizedDefaultValues?.pickup?.zipCode,
      })
        ? StopEntryMode.Location
        : StopEntryMode.Quick
    );
    setConsigneeEntryMode(
      shouldDefaultStopToLocationEntry({
        externalTMSID: memoizedDefaultValues?.consignee?.externalTMSID,
        name: memoizedDefaultValues?.consignee?.name,
        addressLine1: memoizedDefaultValues?.consignee?.addressLine1,
        city: memoizedDefaultValues?.consignee?.city,
        state: memoizedDefaultValues?.consignee?.state,
        zipCode: memoizedDefaultValues?.consignee?.zipCode,
      })
        ? StopEntryMode.Location
        : StopEntryMode.Quick
    );
  }, [
    memoizedDefaultValues?.pickup?.externalTMSID,
    memoizedDefaultValues?.pickup?.name,
    memoizedDefaultValues?.pickup?.addressLine1,
    memoizedDefaultValues?.pickup?.city,
    memoizedDefaultValues?.pickup?.state,
    memoizedDefaultValues?.pickup?.zipCode,
    memoizedDefaultValues?.consignee?.externalTMSID,
    memoizedDefaultValues?.consignee?.name,
    memoizedDefaultValues?.consignee?.addressLine1,
    memoizedDefaultValues?.consignee?.city,
    memoizedDefaultValues?.consignee?.state,
    memoizedDefaultValues?.consignee?.zipCode,
  ]);

  // Helper function to normalize temperature based on equipment type
  const normalizeTemperatureForEquipment = (
    specifications: {
      transportType?: string;
      minTempFahrenheit?: Maybe<number>;
      maxTempFahrenheit?: Maybe<number>;
    },
    equipmentTypes: Undef<TMSEquipmentType[]>,
    isCreateMode: boolean,
    mode: MaybeUndef<string>
  ): { minTempFahrenheit: number; maxTempFahrenheit: number } => {
    // Check if equipment is refrigerated (will use equipmentTypes if available,
    // or fall back to checking transportType string directly)
    const isRefrigerated = isEquipmentRefrigerated(
      specifications.transportType,
      equipmentTypes,
      mode
    );

    // Determine if we should preserve temperature values
    const shouldPreserveTemperature =
      isRefrigerated === true || (isRefrigerated === null && !isCreateMode); // Preserve in edit mode when uncertain

    if (shouldPreserveTemperature) {
      // Preserve temperature values:
      // - If refrigerated: use form values, default to 0 if null/undefined
      // - If uncertain in edit mode: preserve to prevent data loss when API fails
      return {
        minTempFahrenheit: specifications.minTempFahrenheit ?? 0,
        maxTempFahrenheit: specifications.maxTempFahrenheit ?? 0,
      };
    } else {
      // Clear temperature values:
      // - If NOT refrigerated: clear to prevent stale data
      // - If uncertain in create mode: clear to prevent stale data from previous equipment selection
      return {
        minTempFahrenheit: 0,
        maxTempFahrenheit: 0,
      };
    }
  };

  const stopTypeLabel = (stopType: LoadStopTypes): string =>
    stopType === LoadStopTypes.Pickup ? 'Pickup' : 'Consignee';

  const createAISuggestedLocations = async (
    formData: NormalizedLoad
  ): Promise<Maybe<NormalizedLoad>> => {
    const originalSuggestionData = originalLoadBuildingSuggestion;

    if (!originalSuggestionData) {
      return formData;
    }

    const stopsToCheck: Array<{
      stopType: LoadStopTypes;
      entryMode: StopEntryMode;
    }> = [
      { stopType: LoadStopTypes.Pickup, entryMode: pickupEntryMode },
      { stopType: LoadStopTypes.Consignee, entryMode: consigneeEntryMode },
    ];

    const updatedData = { ...formData };
    const createdLocations: Array<{
      location: TMSLocation;
      stopType: LoadStopTypes;
    }> = [];
    const locationCreateFailures: Array<{
      stopType: LoadStopTypes;
      message: string;
    }> = [];

    for (const { stopType, entryMode } of stopsToCheck) {
      // Only auto-create locations for Location Entry flow.
      if (entryMode !== StopEntryMode.Location) {
        continue;
      }

      if (
        hasValidAISuggestedLocation(
          stopType,
          updatedData,
          originalSuggestionData
        )
      ) {
        const stopData = updatedData[stopType];
        if (!stopData) {
          continue;
        }

        const locationPayload: TMSLocation = {
          ID: undefined,
          externalTMSID: '',
          name: stopData.name || '',
          addressLine1: stopData.addressLine1 || '',
          addressLine2: stopData.addressLine2 || '',
          city: stopData.city || '',
          state: stopData.state || '',
          zipCode: stopData.zipCode || '',
          country: stopData.country || 'United States',
          contact: stopData.contact || '',
          phone: stopData.phone || '',
          email: stopData.email || '',
          apptRequired: stopData.apptRequired ?? false,
          isShipper: stopType === LoadStopTypes.Pickup,
          isConsignee: stopType === LoadStopTypes.Consignee,
        };

        const options = stopData.apptNote
          ? { specialInstructions: stopData.apptNote }
          : undefined;

        const res = await createLocation({
          location: locationPayload,
          tmsID: tmsIntegrations[0]?.id,
          ...(options && { options }),
        });

        if (res.isOk()) {
          const newLocation = res.value.location;
          createdLocations.push({ location: newLocation, stopType });

          if (updatedData[stopType]) {
            (updatedData[stopType] as any).externalTMSID =
              newLocation.externalTMSID;
          }

          // The backend may return a location already in the list instead of creating one.
          setLocations((prevLocations) =>
            mergeOrReplaceLocationInList(prevLocations, newLocation)
          );
        } else {
          locationCreateFailures.push({
            stopType,
            message:
              res.error.message ||
              `Error creating ${stopType} location. Please try again.`,
          });
        }
      }
    }

    if (createdLocations.length > 0) {
      createdLocations.forEach(({ location, stopType }) => {
        formMethods.setValue(
          `${stopType}.externalTMSID` as FieldPath<NormalizedLoad>,
          location.externalTMSID,
          {
            shouldDirty: true,
          }
        );
      });
    }

    if (locationCreateFailures.length > 0) {
      const successLines = createdLocations.map(
        ({ stopType }) =>
          `${stopTypeLabel(stopType)}: location created successfully.`
      );
      const failureLines = locationCreateFailures.map(
        ({ stopType, message }) => `${stopTypeLabel(stopType)}: ${message}`
      );
      toast({
        title:
          createdLocations.length > 0
            ? 'Some locations could not be created'
            : 'Locations could not be created',
        description: (
          <span className='whitespace-pre-line'>
            {successLines.length > 0 ? successLines.join('\n') : null}
            {successLines.length > 0 && failureLines.length > 0 ? '\n\n' : null}
            {failureLines.join('\n')}
          </span>
        ),
        variant: createdLocations.length > 0 ? 'warning' : 'destructive',
      });
      // Do not submit the load: partial IDs would leave the request inconsistent (matches Turvo abort).
      return null;
    }

    return updatedData;
  };

  const processFormSubmit = async (
    data: NormalizedLoad,
    options: { bypassDuplicateCheck?: boolean } = {}
  ) => {
    const { bypassDuplicateCheck = false } = options;

    setIsLoading(true);

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Revenova,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      data.pickup?.externalTMSID,
      data.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      data.pickup?.readyTime,
      data.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    if (isCreateMode && builtLoad && !bypassDuplicateCheck) {
      setIsLoading(false);
      setPendingSubmissionData(data);
      setShowDuplicateWarningDialog(true);
      return;
    }

    try {
      if (isCreateMode) {
        const afterLocations = await createAISuggestedLocations(data);
        if (afterLocations === null) {
          return;
        }
        data = afterLocations;
      }

      // Map UI-only name/description fields without conflating item and handling counts.
      if (data.commodities && Array.isArray(data.commodities)) {
        const commodities = isCreateMode
          ? data.commodities.filter((c) => !isBlankRevenovaCommodity(c))
          : data.commodities;
        data.commodities = commodities.map(
          normalizeCommodityForRevenovaAPI
        ) as Commodity[];
      }

      // Handle temperature based on equipment type (same logic as UI)
      if (data.specifications) {
        const normalizedTemp = normalizeTemperatureForEquipment(
          data.specifications,
          equipmentTypes,
          isCreateMode,
          data.mode
        );
        data.specifications.minTempFahrenheit =
          normalizedTemp.minTempFahrenheit;
        data.specifications.maxTempFahrenheit =
          normalizedTemp.maxTempFahrenheit;
      }

      if (isCreateMode) {
        // Create new load
        data.freightTrackingID = '';
        data.externalTMSID = '';

        const cleanedPickup =
          pickupEntryMode === StopEntryMode.Quick
            ? cleanStopDataForQuickEntry(data.pickup)
            : data.pickup;
        const cleanedConsignee =
          consigneeEntryMode === StopEntryMode.Quick
            ? cleanStopDataForQuickEntry(data.consignee)
            : data.consignee;

        const reqData = withLoadBuildingSuggestionContext(
          {
            clickedSuggestion,
            threadId,
            emailId,
            emailExternalId: threadItemId,
          },
          {
            load: {
              ...data,
              pickup: cleanedPickup,
              consignee: cleanedConsignee,
              tmsID: tmsIntegrations[0]?.id,
            } as unknown as Load,
          }
        );

        const res = await guardedCreateLoad(data.customer, reqData, customers);
        if (!res) {
          return;
        }
        if (res.isOk()) {
          setBuiltLoad(res.value.load);
          FormStorageService.clearFormState(`${tmsName}_${threadId}`);
          setCurrentState((prevState) => {
            if (
              clickedSuggestion &&
              clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
            ) {
              return {
                ...prevState,
                ...applyCompleteKitSuggestions(
                  prevState,
                  clickedSuggestion.id,
                  SuggestionPipelines.LoadBuilding
                ),
              };
            }
            return prevState;
          });

          // Extract load ID from multiple sources using helper function
          const loadId = extractLoadId(res.value.load, res.value.message);

          setBuiltLoadId(loadId || null);

          // Check if response includes errorLevel to determine toast variant
          // 'warning' → yellow toast, 'error' → red toast, undefined → green success toast
          const errorLevel = res.value.errorLevel;
          const toastVariant = toastVariantFromErrorLevel(errorLevel);

          // Warning and error toasts stay. A plain success toast is the extra message.
          if (toastVariant !== 'success') {
            const { title: displayTitle, description: displayDescription } =
              formatToastMessage(res.value.message, loadId || null);

            const toastResult = toast({
              title: displayTitle,
              description: displayDescription,
              variant: toastVariant,
            });

            if (errorLevel === 'warning' || errorLevel === 'error') {
              setTimeout(() => {
                toastResult.dismiss();
              }, 15000);
            }
          }
        } else {
          const isWarning = res.error.level === 'warning';

          if (res.error.load && isWarning) {
            setBuiltLoad(res.error.load);
            FormStorageService.clearFormState(`${tmsName}_${threadId}`);
            if (
              clickedSuggestion &&
              clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
            ) {
              setCurrentState((prevState) => ({
                ...prevState,
                ...applyCompleteKitSuggestions(
                  prevState,
                  clickedSuggestion.id,
                  SuggestionPipelines.LoadBuilding
                ),
              }));
            }

            // Extract load ID from multiple sources using helper function
            const loadId = extractLoadId(res.error.load, res.error.message);

            setBuiltLoadId(loadId || null);

            // Format toast message, handling customer quote account status errors specially
            const { title: displayTitle, description: displayDescription } =
              formatToastMessage(res.error.message, loadId || null);

            // Show toast with load ID for warnings (load was created successfully)
            const toastResult = toast({
              title: displayTitle,
              description: displayDescription,
              variant: 'warning',
            });

            // Make warning toast stay longer (15 seconds)
            setTimeout(() => {
              toastResult.dismiss();
            }, 15000);
          } else {
            // Display backend error message for actual errors
            toast({
              description: res.error.message || 'Error building load.',
              variant: isWarning ? 'warning' : 'destructive',
            });
          }
        }
      } else {
        // Edit mode - update existing load
        const denormalizedData = denormalizeDatesForTMSForm(tmsName, data);

        if (!load?.ID) {
          toast({
            description: 'Load ID is required for updates.',
            variant: 'destructive',
          });
          return;
        }

        const res = await updateTMS(load.ID, {
          operation: TMSOperation.UpdateLoad,
          load: denormalizedData as Load,
        });

        if (res.isOk()) {
          // Check if response includes errorLevel to determine toast variant
          // 'warning' → yellow toast, 'error' → red toast, undefined → green success toast
          const errorLevel = res.value.errorLevel;
          const toastVariant = toastVariantFromErrorLevel(errorLevel);
          toast({
            description: res.value.message || 'Load updated successfully!',
            variant: toastVariant,
          });
          await acceptLoadViewSuggestion(
            clickedSuggestion,
            denormalizedData as Load,
            setCurrentState,
            undefined,
            tmsName
          );
        } else {
          // Display backend error message (UserFacingError) - use warning variant for partial success scenarios
          // Use longer duration for error messages so users have time to read them
          const isWarning = res.error.level === 'warning';
          toast({
            description: res.error.message || 'Error updating load.',
            variant: isWarning ? 'warning' : 'destructive',
          });
        }
      }
    } catch {
      toast({
        description: isCreateMode
          ? 'Error building load.'
          : 'Error updating load.',
        variant: 'destructive',
      });
      return;
    } finally {
      setIsLoading(false);
    }

    // Call custom submit handler if provided
    if (onSubmit) {
      onSubmit(data);
    }
  };

  const handleSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    await processFormSubmit(data);
  };

  const handleConfirmedSubmit = async () => {
    const data = pendingSubmissionData ?? formMethods.getValues();

    await processFormSubmit(data, { bypassDuplicateCheck: true });
    setShowDuplicateWarningDialog(false);
    setPendingSubmissionData(null);
  };

  // Helper function to extract load ID from load object and message
  const extractLoadId = (load: Undef<Load>, message: Undef<string>): string => {
    // Try freightTrackingID first, then externalTMSID
    let loadId = load?.freightTrackingID || load?.externalTMSID || '';

    // If load ID is still empty, try to extract it from the message
    // Match "Successfully built load [ID]" or "built load [ID]" to avoid false matches
    // Use \S+ to capture IDs with special characters (hyphens, dots, etc.) until whitespace
    if (!loadId && message) {
      const messageMatch = message.match(
        /(?:Successfully\s+)?built\s+load\s+(\S+)/i
      );
      if (messageMatch && messageMatch[1]) {
        loadId = messageMatch[1];
      }
    }

    return loadId;
  };

  const handleInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.Revenova,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });

    if (onInvalid) {
      onInvalid(errors);
    }
  };

  return (
    <div className='mb-5'>
      <ExtendedFormProvider
        aiDefaultValues={showAIHints}
        aiIconOnly={true}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={formMethods.handleSubmit(handleSubmit, handleInvalid)}
          >
            <LoadBuildingAccordionControls
              activeTabs={activeTabs}
              allTabs={allTabs}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              defaultView={defaultView}
              onDefaultViewChange={setDefaultView}
            />
            <Accordion
              type='multiple'
              value={activeTabs}
              onValueChange={setActiveTabs}
            >
              <LoadSectionAccordionItem
                label='Customer'
                icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.customer}
                activeTabs={activeTabs}
              >
                <CustomerSectionForm
                  formMethods={formMethods}
                  customers={customers || []}
                  setCustomers={setCustomers}
                  isLoadingCustomers={isLoadingCustomers}
                  setIsLoadingCustomers={setIsLoadingCustomers}
                  originalSuggestionData={originalLoadBuildingSuggestion}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Specs'
                icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.specifications}
                activeTabs={activeTabs}
              >
                <SpecificationsForm
                  formMethods={formMethods}
                  isCreateMode={isCreateMode}
                  modeOptions={modeOptions}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Rates'
                icon={
                  <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                }
                name={AvailableTabs.rates}
                activeTabs={activeTabs}
              >
                <RatesForm
                  formMethods={formMethods}
                  lineHaulSource={
                    (originalLoadBuildingSuggestion as Undef<SuggestedLoad>)
                      ?.customerRateSource
                  }
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Pickup'
                icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.pickup}
                activeTabs={activeTabs}
              >
                <StopForm
                  formMethods={formMethods}
                  locations={locations || []}
                  setLocations={setLocations}
                  isLoadingLocations={isLoadingLocations}
                  setIsLoadingLocations={setIsLoadingLocations}
                  stopType={LoadStopTypes.Pickup}
                  isCreateMode={isCreateMode}
                  entryMode={pickupEntryMode}
                  setEntryMode={setPickupEntryMode}
                  originalSuggestionData={originalLoadBuildingSuggestion}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Consignee'
                icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.consignee}
                activeTabs={activeTabs}
              >
                <StopForm
                  formMethods={formMethods}
                  locations={locations || []}
                  setLocations={setLocations}
                  isLoadingLocations={isLoadingLocations}
                  setIsLoadingLocations={setIsLoadingLocations}
                  stopType={LoadStopTypes.Consignee}
                  isCreateMode={isCreateMode}
                  entryMode={consigneeEntryMode}
                  setEntryMode={setConsigneeEntryMode}
                  originalSuggestionData={originalLoadBuildingSuggestion}
                />
              </LoadSectionAccordionItem>

              {!isCreateMode && (
                <LoadSectionAccordionItem
                  label='Bill To'
                  icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.billTo}
                  activeTabs={activeTabs}
                >
                  <BillToSectionForm />
                </LoadSectionAccordionItem>
              )}
            </Accordion>

            <section className='w-full mt-4'>
              <Button
                buttonNamePosthog={
                  isCreateMode
                    ? ButtonNamePosthog.BuildLoad
                    : ButtonNamePosthog.UpdateTMS
                }
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
                logProperties={{ serviceID }}
              >
                {submitButtonLabel(isLoading, isCreateMode)}
              </Button>

              {isCreateMode && formMethods.formState.isDirty && (
                <Flex justify='center' align='center'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='w-50% mt-4 h-8 text-sm text-neutral-500'
                    disabled={isLoading}
                    variant='outline'
                    onClick={handleClearForm}
                    logProperties={{ serviceID }}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}

              {isCreateMode &&
                builtLoad &&
                ((builtLoad.externalTMSID &&
                  builtLoad.externalTMSID.trim() !== '') ||
                  (builtLoad.freightTrackingID &&
                    builtLoad.freightTrackingID.trim() !== '') ||
                  builtLoadId) && (
                  <div className='whitespace-pre-wrap my-3 rounded py-3 text-neutral-800 px-4 bg-success-50'>
                    <Typography className='mb-2'>Load Created 🎉</Typography>
                    {builtLoad.externalTMSID &&
                      builtLoad.externalTMSID.trim() !== '' && (
                        <Typography variant='body-sm' className='mb-2'>
                          <Typography variant='body-sm' weight='bold'>
                            Revenova Load ID:{' '}
                          </Typography>
                          {builtLoad.externalTMSID}
                        </Typography>
                      )}
                    {builtLoad.freightTrackingID &&
                      builtLoad.freightTrackingID.trim() !== '' && (
                        <Typography variant='body-sm' className='mb-2'>
                          <Typography variant='body-sm' weight='bold'>
                            Shipment ID:{' '}
                          </Typography>
                          {builtLoad.freightTrackingID}
                        </Typography>
                      )}
                    {/* Fallback: if neither ID exists but we have a parsed load ID from message */}
                    {(!builtLoad.externalTMSID ||
                      builtLoad.externalTMSID.trim() === '') &&
                      (!builtLoad.freightTrackingID ||
                        builtLoad.freightTrackingID.trim() === '') &&
                      builtLoadId && (
                        <Typography variant='body-sm' className='mb-2'>
                          <Typography variant='body-sm' weight='bold'>
                            Load ID:{' '}
                          </Typography>
                          {builtLoadId}
                        </Typography>
                      )}
                  </div>
                )}
            </section>
          </form>
        </FormProvider>
      </ExtendedFormProvider>

      <DuplicateLoadWarningDialog
        open={showDuplicateWarningDialog}
        onOpenChange={(isOpen) => {
          setShowDuplicateWarningDialog(isOpen);
          if (!isOpen && !isLoading) {
            setPendingSubmissionData(null);
          }
        }}
        onConfirm={handleConfirmedSubmit}
        isLoading={isLoading}
      />
    </div>
  );
}
